import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

const REQUIRED = ["exterior", "interior", "experience"];
const COLORS = { full: "#00FF66", partial: "#FFEA00", none: "#FF0055" };

function statusOf(v) {
  const missing = v.missing?.length ?? REQUIRED.length;
  if (missing === 0) return "full";
  if (missing < REQUIRED.length) return "partial";
  return "none";
}

export default function CoverageMap() {
  const [data, setData] = useState({ summary: {}, venues: [] });
  const [loading, setLoading] = useState(true);
  const [onlyNeeds, setOnlyNeeds] = useState(false);

  useEffect(() => {
    api.get("/admin/media/coverage").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const s = data.summary || {};
  const withGeo = data.venues.filter((v) => v.lat && v.lng);
  const shown = onlyNeeds ? withGeo.filter((v) => statusOf(v) !== "full") : withGeo;
  const needList = data.venues.filter((v) => (v.missing?.length ?? 3) > 0);

  return (
    <div data-testid="coverage-map-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display font-bold text-2xl text-white">Photo Coverage Map</h2>
          <p className="text-neutral-500 text-sm mt-1">Every venue needs <b className="text-white">Exterior, Interior & Experience</b> shots. Red pins still need photos.</p>
        </div>
        <div className="flex items-center gap-3">
          {[["Venues", s.total, "#fff"], ["Covered", s.covered, COLORS.full], ["Need photos", s.uncovered, COLORS.none]].map(([l, v, col]) => (
            <div key={l} className="border border-white/10 bg-[#141414] px-4 py-2 text-center">
              <div className="font-display font-extrabold text-xl" style={{ color: col }}>{v ?? "—"}</div>
              <div className="text-neutral-500 text-[10px]">{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-3 text-xs">
        <label className="flex items-center gap-2 text-neutral-300 cursor-pointer" data-testid="coverage-only-needs">
          <input type="checkbox" checked={onlyNeeds} onChange={(e) => setOnlyNeeds(e.target.checked)} /> Show only venues needing photos
        </label>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: COLORS.full }} /> complete</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: COLORS.partial }} /> partial</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: COLORS.none }} /> needs all</span>
      </div>

      {loading ? <div className="text-neutral-500 py-16 text-center">Loading map…</div> : (
        <div className="border border-white/10" style={{ height: "460px" }} data-testid="coverage-leaflet">
          <MapContainer center={[39.767, -104.982]} zoom={15} style={{ height: "100%", width: "100%", background: "#0f0f0f" }}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {shown.map((v) => {
              const st = statusOf(v);
              return (
                <CircleMarker key={v.slug} center={[v.lat, v.lng]} radius={7}
                  pathOptions={{ color: COLORS[st], fillColor: COLORS[st], fillOpacity: 0.85, weight: 1 }}>
                  <Popup>
                    <div style={{ minWidth: 160 }}>
                      <strong>{v.name}</strong><br />
                      <span style={{ fontSize: 11 }}>{v.category}</span><br />
                      {v.missing.length === 0
                        ? <span style={{ color: "#0a0" }}>All required shots ✓</span>
                        : <span style={{ color: "#c00" }}>Needs: {v.missing.join(", ")}</span>}
                      <br /><a href={`/directory/${v.slug}`} target="_blank" rel="noopener noreferrer">View profile →</a>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>
      )}

      <h3 className="font-accent text-[#00E5FF] text-sm mt-6 mb-2">Venues needing photos ({needList.length})</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[40vh] overflow-y-auto">
        {needList.slice(0, 300).map((v) => (
          <Link key={v.slug} to={`/directory/${v.slug}`} target="_blank" className="border border-white/10 bg-[#141414] px-3 py-2 flex items-center justify-between hover:border-[#FF0055]" data-testid={`need-${v.slug}`}>
            <span className="text-sm text-neutral-200 truncate">{v.name}</span>
            <span className="flex gap-1 flex-shrink-0 ml-2">
              {v.missing.map((m) => <span key={m} className="text-[9px] bg-[#FF0055]/20 text-[#FF0055] px-1">{m.slice(0, 3)}</span>)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
